class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.actions = [];
    this.processing = false;
    this.requestId = 0;
    this.activeElement = null;
    this.protectionAutoFailed = false;
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentFocusin = this.onDocumentFocusin.bind(this);

    this.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('click', (event) => {
      const icon = event.target.closest('#cart-icon-bubble');
      if (!icon) return;
      event.preventDefault();
      this.open(icon);
      this.refresh();
    });
    document.addEventListener('keydown', (event) => {
      if (event.code !== 'Space' || event.target.id !== 'cart-icon-bubble') return;
      event.preventDefault();
      this.open(event.target);
      this.refresh();
    });
    document.addEventListener('submit', (event) => this.onProductSubmit(event));
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async onProductSubmit(event) {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.closest('cart-drawer') && this.processing) { event.preventDefault(); return; }
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add') || form.closest('product-form') || form.querySelector('[data-skip-cart="true"]') || event.defaultPrevented) return;
    event.preventDefault();
    if (form.dataset.cartSubmitting === 'true') return;
    form.dataset.cartSubmitting = 'true';
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');
    const data = new FormData(form);
    data.append('sections', 'cart-drawer,cart-icon-bubble');
    data.append('sections_url', window.location.pathname);
    this.setActiveElement(button || document.activeElement);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Could not add item to cart.');
      this.renderContents(state);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mw-cart-form', cartData: state });
    } catch (error) {
      let message = form.querySelector('[data-cart-form-error]');
      if (!message) {
        message = document.createElement('p');
        message.dataset.cartFormError = '';
        message.setAttribute('role', 'alert');
        message.style.cssText = 'color:#9b3029;font-size:12px;margin:8px 0';
        form.appendChild(message);
      }
      message.textContent = error.message || this.dataset.updateError;
    } finally {
      delete form.dataset.cartSubmitting;
      if (button) button.removeAttribute('aria-busy');
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    else if (!this.classList.contains('active') && !this.activeElement) this.activeElement = document.activeElement;
    if (this.classList.contains('active')) return;
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    this.previousOverflow = document.body.style.overflow;
    this.previousPaddingRight = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('focusin', this.onDocumentFocusin);
    this.querySelector('.drawer__inner')?.focus({ preventScroll: true });
    setTimeout(() => this.querySelector('.drawer__inner')?.focus({ preventScroll: true }), 0);
    this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    this.syncProtection();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this.previousOverflow || '';
    document.body.style.paddingRight = this.previousPaddingRight || '';
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('focusin', this.onDocumentFocusin);
    if (this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
    this.activeElement = null;
    this.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.key !== 'Tab') return;
    const panel = this.querySelector('.drawer__inner');
    const elements = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.getClientRects().length);
    if (!elements.length) { event.preventDefault(); panel.focus(); return; }
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  }

  onDocumentFocusin(event) {
    const panel = this.querySelector('.drawer__inner');
    if (this.classList.contains('active') && panel && !panel.contains(event.target)) panel.focus({ preventScroll: true });
  }

  onClick(event) {
    if (event.target.closest('[data-cart-close], #CartDrawer-Overlay')) { this.close(); return; }
    if (event.target.closest('.mw-cart__checkout') && this.processing) { event.preventDefault(); return; }
    const protection = event.target.closest('[data-cart-protection]');
    if (protection) {
      event.preventDefault();
      const state = this.querySelector('[data-protection-state]');
      const variantId = Number(state?.dataset.protectionVariant);
      if (!variantId) return;
      const enabled = protection.getAttribute('aria-checked') !== 'true';
      protection.setAttribute('aria-checked', String(enabled));
      this.protectionAutoFailed = false;
      this.actions.push({ type: 'protection', variantId, enabled, persistPreference: true });
      this.processChanges();
      return;
    }
    const button = event.target.closest('[data-cart-plus], [data-cart-minus], [data-cart-remove]');
    if (!button) return;
    event.preventDefault();
    const item = button.closest('[data-cart-line]');
    if (!item) return;
    const type = button.hasAttribute('data-cart-plus') ? 'add' : button.hasAttribute('data-cart-remove') ? 'remove' : 'decrease';
    this.actions.push({
      type,
      key: item.dataset.cartKey,
      line: Number(item.dataset.cartLine),
      variantId: Number(item.dataset.cartVariant),
      properties: JSON.parse(item.dataset.cartProperties || '{}'),
      sellingPlan: item.dataset.cartSellingPlan
    });
    const value = item.querySelector('[data-cart-value]');
    const shown = Number(value.textContent);
    value.textContent = type === 'remove' ? 0 : Math.max(0, shown + (type === 'add' ? 1 : -1));
    this.processChanges();
  }

  syncProtection() {
    if (this.processing || this.actions.length || this.protectionAutoFailed) return;
    const state = this.querySelector('[data-protection-state]');
    const variantId = Number(state?.dataset.protectionVariant);
    if (!variantId) return;
    const hasMerchandise = Number(state.dataset.merchandiseCount) > 0;
    if (hasMerchandise && !this.classList.contains('active')) return;
    const preference = state.dataset.protectionPreference;
    const auto = state.dataset.protectionDefault === 'true' && this.classList.contains('active');
    const wanted = hasMerchandise && state.dataset.protectionAvailable === 'true' && (preference === 'on' || (preference !== 'off' && auto));
    if (Number(state.dataset.protectionQuantity) === Number(wanted)) return;
    this.actions.push({ type: 'protection', variantId, enabled: wanted, persistPreference: false });
    this.processChanges();
  }

  setBusy(busy) {
    this.classList.toggle('is-loading', busy);
    this.querySelector('.drawer__inner')?.setAttribute('aria-busy', String(busy));
    const checkout = this.querySelector('.mw-cart__checkout');
    if (checkout) checkout.disabled = busy;
  }

  async processChanges() {
    if (this.processing) return;
    this.processing = true;
    ++this.requestId;
    this.setBusy(true);
    let lastState;
    let activeAction;
    try {
      while (this.actions.length) {
        const action = this.actions.shift();
        activeAction = action;
        const root = window.Shopify?.routes?.root || '/';
        let endpoint;
        let payload;
        if (action.type === 'protection') {
          const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
          if (!cartResponse.ok) throw new Error('Cart lookup failed');
          const cart = await cartResponse.json();
          const hasMerchandise = cart.items.some(item => item.variant_id !== action.variantId);
          const enabled = action.enabled && hasMerchandise;
          const existing = cart.items.filter(item => item.variant_id === action.variantId);
          const updates = {};
          existing.forEach((item, index) => { updates[item.key] = enabled && index === 0 ? 1 : 0; });
          if (!existing.length && enabled) updates[action.variantId] = 1;
          endpoint = 'cart/update.js';
          payload = { updates, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };
          if (action.persistPreference) payload.attributes = { mw_shipping_protection: action.enabled ? 'on' : 'off' };
        } else if (action.type === 'add') {
          endpoint = 'cart/add.js';
          const properties = Array.isArray(action.properties) ? Object.fromEntries(action.properties) : action.properties;
          const item = { id: action.variantId, quantity: 1 };
          if (Object.keys(properties).length) item.properties = properties;
          if (action.sellingPlan) item.selling_plan = Number(action.sellingPlan);
          payload = { items: [item], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };
        } else {
          endpoint = 'cart/change.js';
          const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
          if (!cartResponse.ok) throw new Error('Cart lookup failed');
          const cart = await cartResponse.json();
          const index = cart.items.findIndex(item => item.key === action.key);
          const line = index >= 0 ? index + 1 : action.line;
          const current = cart.items[line - 1];
          if (!current) continue;
          payload = { line, quantity: action.type === 'remove' ? 0 : Math.max(0, current.quantity - 1), sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };
        }
        const response = await fetch(`${root}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart update failed');
        lastState = state;
      }
      if (lastState?.sections?.['cart-drawer']) this.applySections(lastState.sections);
      else await this.refresh();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mw-cart', cartData: lastState });
    } catch (error) {
      if (activeAction?.type === 'protection') this.protectionAutoFailed = true;
      this.actions.length = 0;
      await this.refresh();
      this.showError(error.message || this.dataset.updateError || 'Could not update cart.');
    } finally {
      this.processing = false;
      this.setBusy(false);
      if (this.actions.length) this.processChanges();
      else this.syncProtection();
    }
  }

  showError(message) {
    const error = this.querySelector('.mw-cart__message');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  applySections(sections) {
    if (!sections?.['cart-drawer']) return false;
    const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const next = parsed.querySelector('#CartDrawer .drawer__inner');
    const current = this.querySelector('#CartDrawer .drawer__inner');
    if (!next || !current) return false;
    const wasFocused = current.contains(document.activeElement);
    const protectionWasFocused = wasFocused && document.activeElement.matches('[data-cart-protection]');
    current.innerHTML = next.innerHTML;
    this.classList.toggle('is-empty', !current.querySelector('.mw-cart__item'));
    if (this.classList.contains('active') && (wasFocused || !current.contains(document.activeElement))) {
      const focusTarget = protectionWasFocused ? current.querySelector('[data-cart-protection]') : current;
      (focusTarget || current).focus({ preventScroll: true });
    }
    if (sections['cart-icon-bubble']) {
      const iconDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const icon = iconDoc.querySelector('#cart-icon-bubble');
      const currentIcon = document.querySelector('#cart-icon-bubble');
      if (icon && currentIcon) currentIcon.innerHTML = icon.innerHTML;
    }
    this.setBusy(this.processing);
    this.syncProtection();
    return true;
  }

  renderContents(state) {
    const shouldOpen = Boolean(state?.id || state?.key || state?.items);
    if (state?.sections?.['cart-drawer'] && this.applySections(state.sections)) {
      if (shouldOpen) this.open();
      return;
    }
    this.refresh({ open: shouldOpen });
  }

  async refresh({ open = false } = {}) {
    const requestId = ++this.requestId;
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      if (requestId !== this.requestId) return;
      if (!this.applySections(sections)) throw new Error('Cart section unavailable');
      if (open) this.open();
    } catch (error) {
      if (this.classList.contains('active')) this.showError(this.dataset.updateError || 'Could not refresh cart.');
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
